import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useLocations,
  useWeatherBatch,
  useStories,
  useInfiniteStoriesSuspense,
  useCrossLocationStories,
  useNearbyStories,
  useTrendingStories,
  useStoryCounts,
  useLocationSearch,
  useAstronomical,
  useHourlyForecast,
  useBomAlerts,
  queryKeys,
} from '@weather-story/shared-hooks';
import type { WeatherAlert } from '@weather-story/shared-hooks';
import type { Location } from '@weather-story/shared-types';
import {
  MapPin,
  Sparkles,
  Loader2,
  Search,
  X,
  Star,
  Navigation,
  Clock,
  Globe,
  ArrowLeft,
  CloudRain,
  Cloud,
  Sun,
  Thermometer,
  Droplets,
  Wind,
  Calendar,
  Flame,
  History,
  TrendingUp,
  Wheat,
  CalendarRange,
  AlertTriangle,
  ShieldAlert,
  ExternalLink,
  Satellite,
} from 'lucide-react';
import {
  Fragment,
  lazy,
  Suspense,
  type ReactNode,
  useState,
  useEffect,
  useMemo,
  useRef,
  useTransition,
  useDeferredValue,
} from 'react';
import { useFormStatus } from 'react-dom';
import toast from 'react-hot-toast';
import { useParams, useNavigate, Link } from 'react-router';
import { AdSenseBanner, UpgradeBanner, AdBlockDetector } from '../../../components/ads';
import { FavoriteUpgradeToast } from '../../../components/ads/FavoriteUpgradeToast';
import { DayOfYearBadge } from '../components/DayOfYearBadge';
import { InlineErrorBoundary } from '../../../components/ErrorBoundary';
import { FadeInWhenVisible } from '../../../components/FadeInWhenVisible';
import { SEO } from '../../../components/SEO';
import { StoryCard } from '../components/StoryCard';
import {
  AirQualitySkeleton,
  AstronomicalSkeleton,
  CompareDatesSkeleton,
  NearbyCitiesSkeleton,
  OnThisDaySkeleton,
  StreakHeatmapSkeleton,
  WeeklyForecastSkeleton,
  WillItRainSkeleton,
} from '../../../components/WidgetSkeletons';

// Lazy-loaded heavy widgets — split from the main StoryFeedPage chunk
const ActiveStreaksWidget = lazy(() =>
  import('../components/ActiveStreaksWidget').then((m) => ({ default: m.ActiveStreaksWidget }))
);
const AirQualityWidget = lazy(() =>
  import('../components/AirQualityWidget').then((m) => ({ default: m.AirQualityWidget }))
);
const AstronomicalWidget = lazy(() =>
  import('../components/AstronomicalWidget').then((m) => ({ default: m.AstronomicalWidget }))
);
const CompareDatesWidget = lazy(() =>
  import('../components/CompareDatesWidget').then((m) => ({ default: m.CompareDatesWidget }))
);
const ForecastComparison = lazy(() =>
  import('../components/ForecastComparison').then((m) => ({
    default: m.ForecastComparison,
  }))
);
const NearbyCitiesWidget = lazy(() =>
  import('../components/NearbyCitiesWidget').then((m) => ({ default: m.NearbyCitiesWidget }))
);
const NearestStationFinder = lazy(() =>
  import('../components/NearestStationFinder').then((m) => ({ default: m.NearestStationFinder }))
);
const RadarMiniMap = lazy(() =>
  import('../components/RadarMiniMap').then((m) => ({ default: m.RadarMiniMap }))
);
const OnThisDayWidget = lazy(() =>
  import('../components/OnThisDayWidget').then((m) => ({ default: m.OnThisDayWidget }))
);
const StreakHeatmapWidget = lazy(() =>
  import('../components/StreakHeatmapWidget').then((m) => ({ default: m.StreakHeatmapWidget }))
);
const StreakLeaderboardWidget = lazy(() =>
  import('../components/StreakLeaderboardWidget').then((m) => ({
    default: m.StreakLeaderboardWidget,
  }))
);
const WeatherSiteStrip = lazy(() =>
  import('../components/WeatherSiteStrip').then((m) => ({ default: m.WeatherSiteStrip }))
);
const WeatherTriviaWidget = lazy(() =>
  import('../components/WeatherTriviaWidget').then((m) => ({ default: m.WeatherTriviaWidget }))
);
const WeeklyBestWidget = lazy(() =>
  import('../components/WeeklyBestWidget').then((m) => ({ default: m.WeeklyBestWidget }))
);
const WeeklyForecastGrid = lazy(() =>
  import('../components/WeeklyForecastGrid').then((m) => ({ default: m.WeeklyForecastGrid }))
);
const WillItRainWidget = lazy(() =>
  import('../components/WillItRainWidget').then((m) => ({ default: m.WillItRainWidget }))
);
const YesterdayRecapWidget = lazy(() =>
  import('../components/YesterdayStoriesStrip').then((m) => ({ default: m.YesterdayRecapWidget }))
);
const YesterdayStoriesStrip = lazy(() =>
  import('../components/YesterdayStoriesStrip').then((m) => ({
    default: m.YesterdayStoriesStrip,
  }))
);

import { useVertical } from '../../../contexts/VerticalContext';
import { useShowAds } from '../../../hooks/useShowAds';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import { absoluteUrl } from '../../../lib/jsonLd';
import { api } from '../../../services/api';

/** BOM alert detail modal — full description, affected areas, issue/expiry, BOM link */
function BomAlertModal({ alert, onClose }: { alert: WeatherAlert; onClose: () => void }) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const severityColors = {
    extreme: { badge: 'bg-red-600', text: 'text-red-300', border: 'border-red-500/50' },
    severe: { badge: 'bg-orange-600', text: 'text-orange-300', border: 'border-orange-500/50' },
    moderate: { badge: 'bg-amber-600', text: 'text-amber-300', border: 'border-amber-600/50' },
    minor: { badge: 'bg-yellow-600', text: 'text-yellow-300', border: 'border-yellow-600/40' },
  }[alert.severity];

  const issuedDate = new Date(alert.issueTime);
  const expireDate = alert.expireTime ? new Date(alert.expireTime) : null;
  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className={`bg-gray-800 rounded-xl border ${severityColors.border} w-full max-w-lg max-h-[80vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 pb-0">
          <div className="flex items-center gap-3">
            <ShieldAlert className={`h-6 w-6 ${severityColors.text}`} />
            <div>
              <span
                className={`inline-block text-xs font-bold uppercase px-2 py-0.5 rounded ${severityColors.badge} text-white`}
              >
                {alert.severity}
              </span>
              <span className="ml-2 text-xs text-gray-400 capitalize">{alert.type}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Title */}
        <div className="px-5 pt-3">
          <h3 className="text-base font-semibold text-gray-100 leading-snug">{alert.title}</h3>
        </div>

        {/* Description */}
        {alert.description && (
          <div className="px-5 pt-3">
            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">
              {alert.description}
            </p>
          </div>
        )}

        {/* Metadata */}
        <div className="px-5 pt-4 space-y-2">
          {/* Issue time */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Clock className="h-3.5 w-3.5" />
            <span>Issued: {formatDate(issuedDate)}</span>
          </div>

          {/* Expiry time */}
          {expireDate && (
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <Clock className="h-3.5 w-3.5" />
              <span>Expires: {formatDate(expireDate)}</span>
            </div>
          )}

          {/* Affected areas */}
          {alert.areas.length > 0 && (
            <div className="flex items-start gap-2 text-xs text-gray-400">
              <MapPin className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
              <span>Areas: {alert.areas.join(', ')}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 pt-4">
          <a
            href="https://www.bom.gov.au/australia/warnings/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors"
          >
            View on BOM website
            <ExternalLink className="h-4 w-4" />
          </a>
          <p className="text-xs text-gray-500 mt-2">Source: Bureau of Meteorology</p>
        </div>
      </div>
    </div>
  );
}

/** Combines Suspense + InlineErrorBoundary for every lazy widget. */
function WidgetBoundary({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  return (
    <InlineErrorBoundary>
      <Suspense fallback={fallback ?? null}>{children}</Suspense>
    </InlineErrorBoundary>
  );
}

// Storage keys
const STORAGE_KEY = 'weather-story-location';
const RECENT_KEY = 'weather-story-recent';
const FAVORITES_KEY = 'weather-story-favorites';
const MAX_RECENT = 5;

// Storage helpers
function getStoredLocation(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'melbourne';
  } catch {
    return 'melbourne';
  }
}

function setStoredLocation(locationId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, locationId);
  } catch {}
}

const LOCATION_DETAIL_KEY = 'weather-story-location-detail';

interface CachedLocationDetail {
  id: string;
  name: string;
  state?: string;
  country?: string;
}

function getCachedLocationDetail(locationId: string): CachedLocationDetail | null {
  try {
    const raw = localStorage.getItem(LOCATION_DETAIL_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedLocationDetail;
    return cached.id === locationId ? cached : null;
  } catch {
    return null;
  }
}

function setCachedLocationDetail(loc: CachedLocationDetail): void {
  try {
    localStorage.setItem(LOCATION_DETAIL_KEY, JSON.stringify(loc));
  } catch {}
}

function slugToDisplayName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function getRecentLocations(): string[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
  } catch {
    return [];
  }
}

function addRecentLocation(locationId: string): void {
  try {
    const recent = getRecentLocations().filter((id) => id !== locationId);
    recent.unshift(locationId);
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {}
}

function getFavorites(): string[] {
  try {
    return JSON.parse(localStorage.getItem(FAVORITES_KEY) || '[]');
  } catch {
    return [];
  }
}

function toggleFavorite(locationId: string): string[] {
  try {
    const favorites = getFavorites();
    const index = favorites.indexOf(locationId);
    if (index >= 0) {
      favorites.splice(index, 1);
    } else {
      favorites.push(locationId);
    }
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    return favorites;
  } catch {
    return [];
  }
}

// Find nearest location by coordinates
function findNearestLocation(lat: number, lng: number, locations: Location[]): Location | null {
  if (locations.length === 0) return null;

  let nearest = locations[0];
  let minDistance = Number.MAX_VALUE;

  for (const loc of locations) {
    const distance = Math.sqrt(Math.pow(loc.latitude - lat, 2) + Math.pow(loc.longitude - lng, 2));
    if (distance < minDistance) {
      minDistance = distance;
      nearest = loc;
    }
  }

  return nearest;
}

// React 19: useFormStatus lets this button read its own pending state from the parent <form action>
function GenerateStoriesButton({
  label,
  className,
  isJobRunning,
  jobProgress,
  jobTotal,
  cooldownSeconds,
}: {
  label: string;
  className: string;
  isJobRunning: boolean;
  jobProgress?: number;
  jobTotal?: number;
  cooldownSeconds?: number;
}) {
  const { pending } = useFormStatus();
  const busy = pending || isJobRunning;
  return (
    <button type="submit" disabled={busy || (cooldownSeconds ?? 0) > 0} className={className}>
      {busy ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {isJobRunning && jobProgress != null && jobTotal != null
            ? `Generating (${jobProgress}/${jobTotal})...`
            : 'Generating...'}
        </>
      ) : (cooldownSeconds ?? 0) > 0 ? (
        <>Wait {cooldownSeconds}s</>
      ) : (
        <>
          <Sparkles className="h-4 w-4" />
          {label}
        </>
      )}
    </button>
  );
}

function AllLocationsStories({ locationId }: { locationId: string }) {
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteStoriesSuspense(
    locationId,
    { humor: false }
  );
  const stories = data?.pages?.flatMap((p) => p.stories) ?? [];
  const loadMoreRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return;
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchNextPage();
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (stories.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700 mb-8">
        <Sparkles className="h-12 w-12 text-gray-600 mx-auto mb-4" />
        <p className="text-gray-300 mb-2">No stories available across any locations yet.</p>
        <p className="text-gray-500 text-sm">
          Generate stories from the debug page or select a specific location.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-8">
      <div className="@container">
        <div className="stories-grid">
          {stories.map((story, index) => (
            <div
              key={story.id}
              className="bg-gray-800/50 rounded-xl border border-gray-700/50 h-full flex flex-col"
              style={{ animation: `slideUp 0.22s ease-out ${index * 0.025}s both` }}
            >
              <StoryCard story={story} compact />
            </div>
          ))}
        </div>
      </div>
      <div ref={loadMoreRef} />
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-400 text-sm">Loading more stories...</span>
        </div>
      )}
      {hasNextPage && !isFetchingNextPage && (
        <button
          onClick={() => fetchNextPage()}
          className="w-full py-3 text-sm text-primary-400 hover:text-primary-300 bg-gray-800 rounded-lg border border-gray-700 hover:border-gray-600 transition-colors"
        >
          Load more stories
        </button>
      )}
      {!hasNextPage && stories.length > 0 && (
        <p className="text-center text-gray-500 text-sm py-2">All stories loaded</p>
      )}
    </div>
  );
}

export function StoryFeedPage({ prefetchOnly = false }: { prefetchOnly?: boolean } = {}) {
  const { locationId: urlLocationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const vertical = useVertical();

  // Derive selected location from URL param, falling back to localStorage
  const selectedLocation = urlLocationId || getStoredLocation();

  // Redirect /stories to /stories/{storedLocation} so the URL always reflects the town
  useEffect(() => {
    if (!urlLocationId) {
      navigate(`/stories/${getStoredLocation()}`, { replace: true });
    }
  }, [urlLocationId, navigate]);

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [, startTransition] = useTransition();
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const [hasAutoGenerated, setHasAutoGenerated] = useState<Set<string>>(new Set());
  const [hasAutoFetched, setHasAutoFetched] = useState<Set<string>>(new Set());
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [favorites, setFavorites] = useState<string[]>(getFavorites);
  const [isLocating, setIsLocating] = useState(false);
  const [recentLocations, setRecentLocations] = useState<string[]>(getRecentLocations);
  const [loadTier, setLoadTier] = useState<1 | 2 | 3>(3); // 3 on initial load (all ready)
  const [stormAlertDismissed, setStormAlertDismissed] = useState(false);
  const [previousLocation, setPreviousLocation] = useState<{ id: string; name: string } | null>(
    null
  );
  const [newStoryIds, setNewStoryIds] = useState<Set<string>>(new Set());
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const prevStoryIdsRef = useRef<Set<string>>(new Set());
  const previousLocationTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const fetchWeatherMutateRef = useRef<() => void>(() => {});
  const generateMutateRef = useRef<() => void>(() => {});
  const singleQueryRefetchRef = useRef<() => Promise<unknown>>(() => Promise.resolve());
  const fetchWeatherIsPendingRef = useRef(false);

  const queryClient = useQueryClient();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Navigate helper — updates URL (which drives selectedLocation)
  const setSelectedLocation = (locationId: string) => {
    if (locationId === 'all') {
      navigate('/stories');
    } else {
      navigate(`/stories/${locationId}`);
    }
  };

  // Debounce search query for server-side search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Save location preference, track recent, scroll to top, and animate
  useEffect(() => {
    if (selectedLocation !== 'all') {
      setStoredLocation(selectedLocation);
      addRecentLocation(selectedLocation);
      setRecentLocations(getRecentLocations());
    }

    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Stagger widget loading to reduce request burst
    setLoadTier(1);
    const tier2Timer = setTimeout(() => setLoadTier(2), 300);
    const tier3Timer = setTimeout(() => setLoadTier(3), 600);

    return () => {
      clearTimeout(tier2Timer);
      clearTimeout(tier3Timer);
    };
  }, [selectedLocation]);

  // Fetch available locations
  const { data: locations = [] } = useLocations();

  // Fetch weather for selected location + favorites (immediate, tiny payload)
  const scopedLocationIds = useMemo(() => {
    const ids = new Set<string>();
    if (selectedLocation && selectedLocation !== 'all') ids.add(selectedLocation);
    for (const fav of favorites) ids.add(fav);
    return [...ids];
  }, [selectedLocation, favorites]);

  const { data: scopedWeather } = useWeatherBatch({
    locationIds: scopedLocationIds,
    enabled: scopedLocationIds.length > 0,
  });

  // Fetch full weather batch only when dropdown opens (for temperature previews)
  const { data: fullWeatherData, isLoading: isWeatherLoading } = useWeatherBatch({
    enabled: isDropdownOpen,
  });

  // Merge: prefer full batch when available, fall back to scoped
  const weatherData = fullWeatherData ?? scopedWeather;

  // Fetch story counts per location (for badge in dropdown)
  const { data: storyCounts = {} } = useStoryCounts({ enabled: locations.length > 0 });

  // Server-side search for 844 stations (debounced, min 2 chars)
  const { data: serverSearchResults = [], isFetching: isServerSearching } = useLocationSearch(
    debouncedSearchQuery,
    { limit: 20, enabled: isDropdownOpen }
  );

  // Use server results when searching, otherwise show all locations
  // deferredSearchQuery is used here so the filter re-runs as a non-urgent update,
  // keeping the input responsive while the list re-renders in the background.
  const filteredLocations = useMemo(() => {
    if (!deferredSearchQuery.trim()) return locations;
    // Use server results when available, merge with local matches to include
    // locations already loaded (for instant feedback while server search loads)
    if (debouncedSearchQuery.length >= 2 && serverSearchResults.length > 0) {
      const serverIds = new Set(serverSearchResults.map((loc) => loc.id));
      // Include local matches not in server results (e.g. international cities)
      const q = deferredSearchQuery.toLowerCase();
      const localOnly = locations.filter(
        (loc) =>
          !serverIds.has(loc.id) &&
          (loc.name.toLowerCase().includes(q) ||
            loc.country?.toLowerCase().includes(q) ||
            loc.state?.toLowerCase().includes(q))
      );
      return [...serverSearchResults, ...localOnly];
    }
    // Fallback to client-side filter for < 2 chars
    const q = deferredSearchQuery.toLowerCase();
    return locations.filter(
      (loc) =>
        loc.name.toLowerCase().includes(q) ||
        loc.country?.toLowerCase().includes(q) ||
        loc.state?.toLowerCase().includes(q)
    );
  }, [locations, deferredSearchQuery, debouncedSearchQuery, serverSearchResults]);

  const isAllLocations = selectedLocation === 'all';

  // Build flat list for keyboard navigation
  // When not searching: show only Favorites + Recent + top 20 popular cities
  // When searching: show filtered results (capped at 50 for performance)
  const flatList = useMemo(() => {
    const list: Array<{
      type: 'location' | 'header' | 'all' | 'hint';
      location?: Location;
      label?: string;
    }> = [];
    const isSearching = !!deferredSearchQuery.trim();

    // "All Locations" always first (when not searching)
    if (!isSearching) {
      list.push({ type: 'all', label: 'All Locations' });
    }

    // Favorites first
    const favoriteLocations = filteredLocations.filter((loc) => favorites.includes(loc.id));
    if (favoriteLocations.length > 0 && !isSearching) {
      list.push({ type: 'header', label: 'Favourites' });
      favoriteLocations.forEach((loc) => list.push({ type: 'location', location: loc }));
    }

    // Recent (excluding favorites)
    const recentLocs = recentLocations
      .filter((id) => !favorites.includes(id))
      .map((id) => filteredLocations.find((loc) => loc.id === id))
      .filter(Boolean) as Location[];
    if (recentLocs.length > 0 && !isSearching) {
      list.push({ type: 'header', label: 'Recent' });
      recentLocs.forEach((loc) => list.push({ type: 'location', location: loc }));
    }

    const shownIds = new Set([...favorites, ...recentLocations]);

    if (isSearching) {
      // When searching: show all filtered results (capped at 50)
      const searchResults = filteredLocations.slice(0, 50);
      if (searchResults.length > 0) {
        list.push({ type: 'header', label: `Results (${filteredLocations.length})` });
        searchResults.forEach((loc) => list.push({ type: 'location', location: loc }));
        if (filteredLocations.length > 50) {
          list.push({
            type: 'hint',
            label: `Showing 50 of ${filteredLocations.length} — refine your search`,
          });
        }
      }
    } else {
      // When not searching: show top 20 popular Australian cities
      const popularAustralian = filteredLocations
        .filter((loc) => loc.country === 'Australia' && !shownIds.has(loc.id))
        .sort((a, b) => (b.population ?? 0) - (a.population ?? 0))
        .slice(0, 20);
      if (popularAustralian.length > 0) {
        list.push({ type: 'header', label: 'Popular' });
        popularAustralian.forEach((loc) => list.push({ type: 'location', location: loc }));
      }

      // International (top 10 by population)
      const internationalLocations = filteredLocations
        .filter((loc) => loc.country !== 'Australia' && !shownIds.has(loc.id))
        .sort((a, b) => (b.population ?? 0) - (a.population ?? 0))
        .slice(0, 10);
      if (internationalLocations.length > 0) {
        list.push({ type: 'header', label: 'International' });
        internationalLocations.forEach((loc) => list.push({ type: 'location', location: loc }));
      }

      // Hint to search for more
      const totalLocations = filteredLocations.length;
      if (totalLocations > 30) {
        list.push({
          type: 'hint',
          label: `Search to find all ${totalLocations.toLocaleString()} locations`,
        });
      }
    }

    return list;
  }, [filteredLocations, favorites, recentLocations, deferredSearchQuery]);

  // Get only navigable items (locations + all) for keyboard navigation
  const navigableItems = flatList.filter((item) => item.type === 'location' || item.type === 'all');

  // Get selected location details
  const selectedLocationData = locations.find((loc) => loc.id === selectedLocation);

  // Fallback display name: localStorage cache → slug-derived
  const cachedDetail =
    selectedLocation && selectedLocation !== 'all'
      ? getCachedLocationDetail(selectedLocation)
      : null;
  const displayName = selectedLocationData
    ? `${selectedLocationData.name}, ${selectedLocationData.state || selectedLocationData.country}`
    : cachedDetail
      ? `${cachedDetail.name}${cachedDetail.state ? `, ${cachedDetail.state}` : ''}`
      : selectedLocation && selectedLocation !== 'all'
        ? slugToDisplayName(selectedLocation)
        : null;

  // Cache location details when API data loads
  useEffect(() => {
    if (selectedLocationData) {
      setCachedLocationDetail({
        id: selectedLocationData.id,
        name: selectedLocationData.name,
        state: selectedLocationData.state ?? undefined,
        country: selectedLocationData.country ?? undefined,
      });
    }
  }, [selectedLocationData]);

  // Fetch stories (single location)
  const singleQuery = useStories(selectedLocation, {
    humor: false,
    enabled: !isAllLocations,
  });

  // Single-location interface (all-locations mode is handled by AllLocationsStories via Suspense)
  const stories = singleQuery.data ?? [];
  const isLoading = singleQuery.isLoading;
  const isFetching = singleQuery.isFetching;

  // Small towns often have no generated stories yet — offer distance-ranked stories
  // from neighbouring towns instead of a dead-end empty state.
  const { data: nearbyStories = [] } = useNearbyStories(selectedLocation, {
    enabled: !isAllLocations && !!selectedLocation && !isLoading && stories.length === 0,
  });

  // Fetch astronomical data for badge (sunrise/sunset)
  const { data: astroData } = useAstronomical(selectedLocation, {
    enabled: !isAllLocations && !!selectedLocation,
    latitude: selectedLocationData?.latitude,
  });

  // Fetch hourly forecast to detect incoming storms in next 48h
  const { data: hourlyForecastData } = useHourlyForecast(selectedLocation, {
    enabled: !isAllLocations && !!selectedLocation,
    hours: 48,
  });
  const hourlyForecast = hourlyForecastData?.forecast ?? [];

  // Detect thunderstorm or heavy_rain with >70% probability in the next 48h
  const stormAlert = useMemo(() => {
    if (!hourlyForecast.length) return null;
    const now = Date.now();
    const cutoff = now + 48 * 60 * 60 * 1000;
    const stormHour = hourlyForecast.find((h) => {
      const t = new Date(h.time).getTime();
      if (t < now || t > cutoff) return false;
      const isStorm = h.conditions === 'thunderstorm' || h.conditions === 'heavy_rain';
      const highProbability = (h.precipitationProbability ?? 0) >= 70;
      return isStorm && highProbability;
    });
    if (!stormHour) return null;

    const localTime = new Date(stormHour.time);
    const hoursFromNow = Math.round((localTime.getTime() - now) / (60 * 60 * 1000));
    const today = new Date(now);
    const isToday = localTime.toDateString() === today.toDateString();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const isTomorrow = localTime.toDateString() === tomorrow.toDateString();
    const timeStr = localTime.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
    const timeLabel =
      hoursFromNow <= 1
        ? 'within the hour'
        : hoursFromNow < 6
          ? `in ~${hoursFromNow}h`
          : isToday
            ? timeStr
            : isTomorrow
              ? `tomorrow ${timeStr}`
              : `${localTime.toLocaleDateString('en-AU', { weekday: 'short' })} ${timeStr}`;

    return {
      condition: stormHour.conditions as string,
      probability: stormHour.precipitationProbability ?? 70,
      timeLabel,
      isThunderstorm: stormHour.conditions === 'thunderstorm',
    };
  }, [hourlyForecast]);

  // BOM official weather warnings
  const { data: bomAlertsData } = useBomAlerts(selectedLocation, {
    enabled: !isAllLocations && !!selectedLocation,
  });
  const bomAlerts = bomAlertsData?.alerts ?? [];
  const [bomAlertsDismissed, setBomAlertsDismissed] = useState(false);
  const [selectedBomAlert, setSelectedBomAlert] = useState<WeatherAlert | null>(null);

  // Reset storm alert dismiss when location changes
  useEffect(() => {
    setStormAlertDismissed(false);
    setBomAlertsDismissed(false);
  }, [selectedLocation]);

  // Ad system: show ads only to free-tier / unauthenticated users
  const showAds = useShowAds();
  const { maxFavorites } = useSubscriptionLimits();

  // Fetch trending stories (Tier 3 — replaces cross-location when available)
  const { data: trendingStories = [] } = useTrendingStories({
    excludeLocationId: selectedLocation,
    limit: 6,
    enabled: !isAllLocations && !!selectedLocation && loadTier >= 3,
  });

  // Fetch cross-location stories as fallback (Tier 3 — low priority, below the fold)
  const { data: crossLocationStories = [] } = useCrossLocationStories({
    excludeLocationId: selectedLocation,
    enabled: !isAllLocations && !!selectedLocation && loadTier >= 3 && trendingStories.length === 0,
  });

  // Fetch weather mutation (for current location only)
  // Also triggers streak auto-init on the server side
  const fetchWeatherMutation = useMutation({
    mutationFn: () => api.post('/weather-data/fetch-weather', { locationId: selectedLocation }),
    onSuccess: async () => {
      // Force-refetch weather data (the mutation updated this on the server)
      queryClient.invalidateQueries({ queryKey: queryKeys.weather.batch() });
      queryClient.invalidateQueries({ queryKey: queryKeys.weather.current(selectedLocation) });
      // Mark stale only — don't force immediate re-fetch (widgets pick it up when they mount/become active)
      queryClient.invalidateQueries({
        queryKey: queryKeys.streaks.active(selectedLocation),
        refetchType: 'none',
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.astronomical.data(selectedLocation),
        refetchType: 'none',
      });
      // Historical backfill runs async on server — refetch On This Day after a delay
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.onThisDay.data(selectedLocation) });
      }, 5000);

      // Auto-generate stories if none exist (chained directly to avoid race conditions)
      const currentStories = queryClient.getQueryData<unknown[]>(
        queryKeys.stories.daily(selectedLocation, false)
      );
      if (
        (!currentStories || currentStories.length === 0) &&
        !hasAutoGenerated.has(selectedLocation)
      ) {
        setHasAutoGenerated((prev) => new Set(prev).add(selectedLocation));
        generateMutation.mutate();
      }
    },
  });

  // Also refetch On This Day after a longer delay to catch async historical backfill
  useEffect(() => {
    if (!isAllLocations && hasAutoFetched.has(selectedLocation)) {
      const timer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.onThisDay.data(selectedLocation) });
      }, 15000);
      return () => clearTimeout(timer);
    }
  }, [selectedLocation, hasAutoFetched, isAllLocations, queryClient]);

  // Generate stories mutation — returns 202 with jobId
  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post<{ jobId: string }>('/weather-data/generate-stories', {
        locationId: selectedLocation,
        maxNew: 3,
      });
      return response.data;
    },
    onMutate: () => {
      prevStoryIdsRef.current = new Set(stories.map((s) => s.id));
    },
    onSuccess: (data) => {
      if (data?.jobId) {
        setActiveJobId(data.jobId);
      }
    },
    onError: () => {
      toast.error('Failed to start story generation');
    },
  });

  // Poll job status while a generation is active
  const jobStatusQuery = useQuery<{
    status: string;
    progress: number;
    total: number;
    error?: string;
  }>({
    queryKey: ['generate-stories-job', activeJobId],
    queryFn: async () => {
      const response = await api.get<{
        status: string;
        progress: number;
        total: number;
        error?: string;
      }>(`/weather-data/generate-stories/status/${activeJobId}`);
      return response.data!;
    },
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : 2000;
    },
  });

  // Keep refs in sync with latest mutation/query methods (avoids stale closures in effects)
  useEffect(() => {
    fetchWeatherMutateRef.current = () => fetchWeatherMutation.mutate();
    generateMutateRef.current = () => generateMutation.mutate();
    singleQueryRefetchRef.current = () => singleQuery.refetch();
    fetchWeatherIsPendingRef.current = fetchWeatherMutation.isPending;
  });

  // Handle job completion/failure
  useEffect(() => {
    if (!jobStatusQuery.data || !activeJobId) return;
    const job = jobStatusQuery.data;

    if (job.status === 'completed') {
      singleQueryRefetchRef.current().then((result: unknown) => {
        const typedResult = result as { data?: Array<{ id: string }> };
        const refreshed = typedResult.data ?? [];
        const freshIds = new Set<string>(
          refreshed.filter((s) => !prevStoryIdsRef.current.has(s.id)).map((s) => s.id)
        );
        if (freshIds.size > 0) {
          setNewStoryIds(freshIds);
          toast.success(
            `${freshIds.size} new ${freshIds.size === 1 ? 'story' : 'stories'} generated!`
          );
          setTimeout(() => setNewStoryIds(new Set()), freshIds.size * 200 + 600);
        } else {
          toast('No new unique stories to generate', { icon: '💡' });
        }
      });
      setActiveJobId(null);
      setCooldownSeconds(30);
    } else if (job.status === 'failed') {
      toast.error(job.error || 'Story generation failed');
      setActiveJobId(null);
    }
  }, [jobStatusQuery.data, activeJobId]);

  const isGenerating = generateMutation.isPending || !!activeJobId;

  // Cooldown timer
  useEffect(() => {
    if (cooldownSeconds <= 0) return;
    const timer = setTimeout(() => setCooldownSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldownSeconds]);
  const isFetchingWeather = fetchWeatherMutation.isPending;

  // Get current location weather
  const currentWeather = weatherData?.[selectedLocation] as Record<string, unknown> | undefined;
  const hasWeatherData = !!(
    currentWeather &&
    (currentWeather.temperature || currentWeather.tempMax)
  );

  // Auto-fetch weather when visiting a location with no data
  useEffect(() => {
    if (
      !isAllLocations &&
      !hasWeatherData &&
      !isFetchingWeather &&
      !isWeatherLoading &&
      !hasAutoFetched.has(selectedLocation)
    ) {
      setHasAutoFetched((prev) => new Set(prev).add(selectedLocation));
      fetchWeatherMutateRef.current();
    }
  }, [
    selectedLocation,
    hasWeatherData,
    isFetchingWeather,
    isWeatherLoading,
    isAllLocations,
    hasAutoFetched,
  ]);

  // Background auto-refresh weather every 15 minutes (silent — no toasts or story generation)
  useEffect(() => {
    if (isAllLocations || !hasWeatherData) return;

    const REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutes
    const interval = setInterval(() => {
      if (!fetchWeatherIsPendingRef.current) {
        // Silently refresh weather data via the batch query invalidation
        queryClient.invalidateQueries({ queryKey: queryKeys.weather.batch() });
        queryClient.invalidateQueries({ queryKey: queryKeys.weather.current(selectedLocation) });
      }
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [selectedLocation, isAllLocations, hasWeatherData, queryClient]);

  // Auto-generate stories when none exist AND weather data already available
  // (handles the case where weather was previously fetched but stories weren't generated)
  useEffect(() => {
    if (
      !isAllLocations &&
      !isLoading &&
      !isFetching &&
      stories.length === 0 &&
      !isGenerating &&
      !isFetchingWeather &&
      hasWeatherData &&
      !hasAutoGenerated.has(selectedLocation)
    ) {
      setHasAutoGenerated((prev) => new Set(prev).add(selectedLocation));
      generateMutateRef.current();
    }
  }, [
    isLoading,
    isFetching,
    stories.length,
    selectedLocation,
    isGenerating,
    hasAutoGenerated,
    hasWeatherData,
    isAllLocations,
    isFetchingWeather,
  ]);

  // Batch-fetch weather for favorited locations that lack data (runs once on load)
  const hasBatchFetchedFavorites = useRef(false);
  useEffect(() => {
    if (hasBatchFetchedFavorites.current || isWeatherLoading || favorites.length === 0) return;
    hasBatchFetchedFavorites.current = true;

    const favoritesWithoutWeather = favorites.filter((id) => {
      const w = weatherData?.[id] as Record<string, unknown> | undefined;
      return !w || (!w.temperature && !w.tempMax);
    });

    if (favoritesWithoutWeather.length === 0) return;

    // Fetch sequentially to avoid hammering Open-Meteo
    (async () => {
      for (const locId of favoritesWithoutWeather) {
        try {
          await api.post('/weather-data/fetch-weather', { locationId: locId });
          setHasAutoFetched((prev) => new Set(prev).add(locId));
        } catch {
          // Non-critical — skip silently
        }
      }
      // Refresh batch data so temps appear in dropdown
      queryClient.invalidateQueries({ queryKey: queryKeys.weather.batch() });
    })();
  }, [isWeatherLoading, favorites, weatherData, queryClient]);

  // Handle location selection
  const handleLocationSelect = (locationId: string) => {
    setSelectedLocation(locationId);
    setIsDropdownOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  // Prefetch stories on hover (debounced 100ms to avoid burst when scrolling)
  const prefetchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [prefetchingLocationId, setPrefetchingLocationId] = useState<string | null>(null);

  const handlePrefetch = (locationId: string) => {
    clearTimeout(prefetchTimerRef.current);
    if (locationId === selectedLocation || locationId === 'all') {
      setPrefetchingLocationId(null);
      return;
    }
    // Skip if already cached
    const cached = queryClient.getQueryData(queryKeys.stories.daily(locationId, false));
    if (cached) {
      setPrefetchingLocationId(null);
      return;
    }
    prefetchTimerRef.current = setTimeout(() => {
      setPrefetchingLocationId(locationId);
      queryClient
        .prefetchQuery({
          queryKey: queryKeys.stories.daily(locationId, false),
          queryFn: async () => {
            const response = await api.get<unknown[]>('/stories/daily', {
              locationId,
              humor: 'false',
            });
            return response.data ?? [];
          },
          staleTime: 5 * 60 * 1000,
        })
        .then(() => {
          setPrefetchingLocationId((prev) => (prev === locationId ? null : prev));
        });
    }, 100);
  };

  const handlePrefetchLeave = () => {
    clearTimeout(prefetchTimerRef.current);
    setPrefetchingLocationId(null);
  };

  // Handle favorite toggle (with subscription-based limit)
  const handleToggleFavorite = (e: React.MouseEvent, locationId: string) => {
    e.stopPropagation();
    const current = getFavorites();
    const isRemoving = current.includes(locationId);
    if (!isRemoving && current.length >= maxFavorites) {
      toast.custom(
        (t) => (
          <div
            className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-sm w-full bg-white shadow-lg rounded-lg pointer-events-auto p-4`}
          >
            <FavoriteUpgradeToast maxFavorites={maxFavorites} />
          </div>
        ),
        { duration: 5000 }
      );
      return;
    }
    setFavorites(toggleFavorite(locationId));
  };

  // Geolocation
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }

    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nearest = findNearestLocation(
          position.coords.latitude,
          position.coords.longitude,
          locations
        );
        if (nearest) {
          handleLocationSelect(nearest.id);
        }
        setIsLocating(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please select manually.');
        setIsLocating(false);
      },
      { timeout: 10000 }
    );
  };

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        startTransition(() =>
          setHighlightedIndex((prev) => (prev < navigableItems.length - 1 ? prev + 1 : 0))
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        startTransition(() =>
          setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : navigableItems.length - 1))
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          const item = navigableItems[highlightedIndex];
          if (item?.type === 'all') {
            handleLocationSelect('all');
          } else if (item?.location) {
            handleLocationSelect(item.location.id);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-location-item]');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  // Get temperature for a location — only show if data is from today or yesterday
  const getTemp = (locationId: string): number | null => {
    const weather = weatherData?.[locationId] as Record<string, unknown> | undefined;
    if (!weather) return null;
    // Check freshness: only show temp if weather date is today or yesterday
    const weatherDate = weather.date as string | undefined;
    if (weatherDate) {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const dataDate = new Date(weatherDate + 'T00:00:00');
      const diffMs = today.getTime() - dataDate.getTime();
      if (diffMs > 2 * 24 * 60 * 60 * 1000) return null; // older than yesterday
    }
    return (weather.temperature ?? weather.tempMax ?? weather.temperatureMax ?? null) as
      number | null;
  };

  // Inline weather data for the combined header
  const headerTemp = hasWeatherData
    ? Math.round((currentWeather?.temperature ?? currentWeather?.tempMax ?? 0) as number)
    : null;
  // Derive today's min/max from hourly forecast when DB values are missing
  // Compare in local timezone (forecast times are UTC, user sees local dates)
  const forecastMinMax = useMemo(() => {
    if (!hourlyForecast.length) return null;
    const now = new Date();
    const todayLocal = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const todayHours = hourlyForecast.filter((h) => {
      const d = new Date(h.time);
      const localDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      return localDate === todayLocal && h.temp != null;
    });
    if (!todayHours.length) return null;
    return {
      max: Math.max(...todayHours.map((h) => h.temp)),
      min: Math.min(...todayHours.map((h) => h.temp)),
    };
  }, [hourlyForecast]);

  const rawTempMax = currentWeather?.temperatureMax ?? currentWeather?.tempMax;
  const rawTempMin = currentWeather?.temperatureMin ?? currentWeather?.tempMin;
  const headerTempMax =
    rawTempMax != null
      ? Math.round(rawTempMax as number)
      : forecastMinMax
        ? Math.round(forecastMinMax.max)
        : null;
  const headerTempMin =
    rawTempMin != null
      ? Math.round(rawTempMin as number)
      : forecastMinMax
        ? Math.round(forecastMinMax.min)
        : null;
  const headerCondition = (currentWeather?.condition ?? currentWeather?.conditions ?? '') as string;
  const headerHumidity = currentWeather?.humidity as number | undefined;
  const headerPrecipitation = currentWeather?.precipitation as number | undefined;
  const headerWindSpeed = currentWeather?.windSpeedMax as number | undefined;

  const getWeatherIcon = (condition: string) => {
    const cond = condition.toLowerCase();
    if (cond.includes('rain') || cond.includes('drizzle'))
      return <CloudRain className="h-8 w-8 sm:h-10 sm:w-10 text-blue-300" />;
    if (cond.includes('cloud') || cond.includes('overcast'))
      return <Cloud className="h-8 w-8 sm:h-10 sm:w-10 text-gray-200" />;
    if (cond.includes('clear') || cond.includes('sunny'))
      return <Sun className="h-8 w-8 sm:h-10 sm:w-10 text-yellow-300" />;
    if (cond.includes('snow')) return <Cloud className="h-8 w-8 sm:h-10 sm:w-10 text-cyan-200" />;
    if (cond.includes('fog') || cond.includes('mist'))
      return <Cloud className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300" />;
    return <Cloud className="h-8 w-8 sm:h-10 sm:w-10 text-gray-300" />;
  };

  const locationDisplayName =
    selectedLocationData?.name ??
    cachedDetail?.name ??
    (selectedLocation && selectedLocation !== 'all' ? slugToDisplayName(selectedLocation) : null);
  const locationTitle = locationDisplayName
    ? `${locationDisplayName} Weather Stories`
    : 'Weather Stories';
  const locationDesc = locationDisplayName
    ? `Daily AI-generated weather stories, historical comparisons, and anomalies for ${displayName || locationDisplayName}.`
    : 'Daily AI-generated weather stories for Australian cities.';
  const storiesJsonLd =
    stories.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: locationTitle,
          description: locationDesc,
          itemListElement: stories.slice(0, 20).map((story, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: story.title,
            url: absoluteUrl(vertical, `/story/${story.id}`),
          })),
        }
      : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Skip SEO when this is the hidden home-page prefetch instance — otherwise its
          Helmet would clobber the visible HomePage's title/og tags (last render wins). */}
      {!prefetchOnly && (
        <SEO
          title={locationTitle}
          description={locationDesc}
          canonical={urlLocationId ? `/stories/${urlLocationId}` : '/stories'}
          jsonLd={storiesJsonLd}
        />
      )}
      {/* Combined Header Card */}
      <div className="bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-4 sm:p-5 text-white shadow-lg mb-8">
        {/* Row 1: Title + Date info */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h1 className="text-lg sm:text-xl font-display font-bold">Today's Stories</h1>
            <DayOfYearBadge
              className="text-white/70"
              scrollTargetId={!isAllLocations ? 'on-this-day' : undefined}
              sunrise={astroData?.dayLength?.sunrise}
              sunset={astroData?.dayLength?.sunset}
              civilTwilightStart={astroData?.dayLength?.civilTwilightStart}
              civilTwilightEnd={astroData?.dayLength?.civilTwilightEnd}
              dayLengthFormatted={astroData?.dayLength?.dayLengthFormatted}
            />
          </div>
        </div>

        {/* Row 2: Location selector + weather details */}
        <div className="mt-3 pt-3 border-t border-white/15">
          {/* Location Selector — prominent */}
          <div className="relative min-w-0 w-full" onKeyDown={handleKeyDown}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 bg-white/20 hover:bg-white/30 rounded-xl px-4 py-3 w-full text-left transition-colors border border-white/10"
            >
              {isAllLocations ? (
                <Globe className="h-5 w-5 text-white/80 flex-shrink-0" />
              ) : (
                <MapPin className="h-5 w-5 text-white/80 flex-shrink-0" />
              )}
              <span className="text-white truncate text-base font-semibold flex-1">
                {isAllLocations ? (
                  'All Locations'
                ) : (
                  <span key={displayName} className="animate-[fadeIn_0.15s_ease-out]">
                    {displayName || 'Select location'}
                  </span>
                )}
              </span>
              <Search className="h-4 w-4 text-white/50 flex-shrink-0" />
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full left-0 mt-1 w-[calc(100vw-2rem)] sm:w-96 bg-gray-800 rounded-lg border border-gray-700 shadow-xl z-50 max-h-[28rem] overflow-hidden">
                {/* Search Input */}
                <div className="p-3 border-b border-gray-700">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={`Search ${locations.length.toLocaleString()} locations...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-8 py-2 bg-gray-900 border border-gray-700 rounded-lg text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      autoFocus
                    />
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                      {isServerSearching && (
                        <Loader2 className="h-3 w-3 text-gray-400 animate-spin" />
                      )}
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="text-gray-400 hover:text-gray-200"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Use My Location Button */}
                  <button
                    onClick={handleUseMyLocation}
                    disabled={isLocating}
                    className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-primary-400 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isLocating ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Navigation className="h-4 w-4" />
                    )}
                    {isLocating ? 'Finding your location...' : 'Use my location'}
                  </button>
                </div>

                {/* Location List */}
                <div ref={listRef} className="max-h-80 overflow-y-auto bg-gray-800">
                  {flatList.map((item) => {
                    if (item.type === 'all') {
                      const navIndex = navigableItems.findIndex((n) => n.type === 'all');
                      const isHighlighted = navIndex === highlightedIndex;
                      return (
                        <button
                          key="all-locations"
                          data-location-item
                          onClick={() => handleLocationSelect('all')}
                          className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 transition-colors border-b border-gray-700 ${
                            isHighlighted
                              ? 'bg-primary-600 text-white'
                              : isAllLocations
                                ? 'bg-primary-900/50 text-primary-300'
                                : 'text-gray-200 hover:bg-gray-700'
                          }`}
                        >
                          <Globe className="h-4 w-4 text-primary-400" />
                          <span className="flex-1 font-medium">All Locations</span>
                          {isAllLocations && (
                            <span className="text-primary-400 font-medium">✓</span>
                          )}
                        </button>
                      );
                    }

                    if (item.type === 'header') {
                      return (
                        <div
                          key={`header-${item.label}`}
                          className="px-3 py-2 text-xs font-semibold text-gray-400 bg-gray-900 sticky top-0 flex items-center gap-2"
                        >
                          {item.label === 'Favourites' && <Star className="h-3 w-3" />}
                          {item.label === 'Recent' && <Clock className="h-3 w-3" />}
                          {item.label?.startsWith('Results') && <Search className="h-3 w-3" />}
                          {item.label === 'Popular' && <TrendingUp className="h-3 w-3" />}
                          {item.label}
                        </div>
                      );
                    }

                    if (item.type === 'hint') {
                      return (
                        <div
                          key={`hint-${item.label}`}
                          className="px-3 py-3 text-xs text-gray-500 text-center italic border-t border-gray-700/50"
                        >
                          {item.label}
                        </div>
                      );
                    }

                    const loc = item.location!;
                    const navIndex = navigableItems.findIndex((n) => n.location?.id === loc.id);
                    const isHighlighted = navIndex === highlightedIndex;
                    const isFavorite = favorites.includes(loc.id);
                    const temp = getTemp(loc.id);

                    const isPrefetching = prefetchingLocationId === loc.id;

                    return (
                      <button
                        key={loc.id}
                        data-location-item
                        onClick={() => handleLocationSelect(loc.id)}
                        onMouseEnter={() => handlePrefetch(loc.id)}
                        onMouseLeave={handlePrefetchLeave}
                        className={`w-full px-3 py-2.5 text-left text-sm flex items-center gap-2 transition-colors relative ${
                          isHighlighted
                            ? 'bg-primary-600 text-white'
                            : loc.id === selectedLocation
                              ? 'bg-primary-900/50 text-primary-300'
                              : 'text-gray-200 hover:bg-gray-700'
                        } ${isPrefetching ? 'overflow-hidden' : ''}`}
                      >
                        {isPrefetching && (
                          <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
                        )}
                        <button
                          onClick={(e) => handleToggleFavorite(e, loc.id)}
                          className={`p-0.5 rounded hover:bg-gray-600 transition-colors ${
                            isFavorite ? 'text-yellow-500' : 'text-gray-500 hover:text-gray-300'
                          }`}
                        >
                          <Star className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                        </button>
                        <span className="flex-1 truncate">
                          {loc.name}, {loc.state || loc.country}
                          {loc.population ? (
                            <span className="ml-1 text-[10px] text-gray-500 font-normal">
                              {loc.population >= 1000
                                ? `${Math.round(loc.population / 1000)}k`
                                : loc.population}
                            </span>
                          ) : null}
                        </span>
                        {temp !== null && (
                          <span className="text-xs font-medium text-gray-500 tabular-nums">
                            {Math.round(temp)}°C
                          </span>
                        )}
                        {(storyCounts as Record<string, number>)[loc.id] > 0 && (
                          <span className="text-xs bg-primary-900/60 text-primary-300 px-1.5 py-0.5 rounded-full tabular-nums">
                            {(storyCounts as Record<string, number>)[loc.id]}
                          </span>
                        )}
                        {loc.id === selectedLocation && (
                          <span className="text-primary-400 font-medium">✓</span>
                        )}
                      </button>
                    );
                  })}

                  {filteredLocations.length === 0 && (
                    <div className="px-3 py-6 text-sm text-gray-400 text-center">
                      No locations found for "{searchQuery}"
                    </div>
                  )}
                </div>

                {/* Keyboard hint */}
                <div className="px-3 py-2 border-t border-gray-700 text-xs text-gray-500 flex gap-4">
                  <span>↑↓ Navigate</span>
                  <span>Enter Select</span>
                  <span>Esc Close</span>
                </div>
              </div>
            )}

            {/* Click outside to close */}
            {isDropdownOpen && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => {
                  setIsDropdownOpen(false);
                  setSearchQuery('');
                  setHighlightedIndex(-1);
                }}
              />
            )}
          </div>

          {/* Weather summary — temp + stats inline */}
          {!isAllLocations && hasWeatherData && (
            <div className="flex items-center gap-4 mt-3">
              {getWeatherIcon(headerCondition)}
              <div>
                <span className="text-3xl sm:text-4xl font-bold">{headerTemp}°C</span>
                <div className="text-white/70 text-sm capitalize">
                  {headerCondition?.replace(/_/g, ' ') || 'No condition data'}
                </div>
              </div>
              <div className="hidden sm:flex items-center gap-4 ml-4 pl-4 border-l border-white/20 text-sm text-white/80">
                {headerTempMax != null && headerTempMin != null && (
                  <div className="flex items-center gap-1.5">
                    <Thermometer className="h-3.5 w-3.5 text-white/50" />
                    <span>
                      {headerTempMax}° / {headerTempMin}°
                    </span>
                  </div>
                )}
                {headerHumidity != null && (
                  <div className="flex items-center gap-1.5">
                    <Droplets className="h-3.5 w-3.5 text-white/50" />
                    <span>{headerHumidity}%</span>
                  </div>
                )}
                {headerPrecipitation != null && headerPrecipitation > 0 && (
                  <div className="flex items-center gap-1.5">
                    <CloudRain className="h-3.5 w-3.5 text-white/50" />
                    <span>{headerPrecipitation}mm</span>
                  </div>
                )}
                {headerWindSpeed != null && (
                  <div className="flex items-center gap-1.5">
                    <Wind className="h-3.5 w-3.5 text-white/50" />
                    <span>{Math.round(headerWindSpeed)} km/h</span>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Mobile-only stats row */}
          {!isAllLocations && hasWeatherData && (
            <div className="flex sm:hidden flex-wrap gap-x-5 gap-y-1 text-sm text-white/80 mt-2">
              <div className="flex items-center gap-1.5">
                <Thermometer className="h-3.5 w-3.5 text-white/50" />
                <span>
                  {headerTempMax}° / {headerTempMin}°
                </span>
              </div>
              {headerHumidity != null && (
                <div className="flex items-center gap-1.5">
                  <Droplets className="h-3.5 w-3.5 text-white/50" />
                  <span>{headerHumidity}%</span>
                </div>
              )}
              {headerPrecipitation != null && headerPrecipitation > 0 && (
                <div className="flex items-center gap-1.5">
                  <CloudRain className="h-3.5 w-3.5 text-white/50" />
                  <span>{headerPrecipitation}mm</span>
                </div>
              )}
              {headerWindSpeed != null && (
                <div className="flex items-center gap-1.5">
                  <Wind className="h-3.5 w-3.5 text-white/50" />
                  <span>{Math.round(headerWindSpeed)} km/h</span>
                </div>
              )}
            </div>
          )}
          {!isAllLocations && !hasWeatherData && !isFetchingWeather && (
            <span className="text-white/60 text-sm mt-3">No weather data yet</span>
          )}
          {!isAllLocations && isFetchingWeather && !hasWeatherData && (
            <div className="flex items-center gap-3 mt-3 animate-pulse">
              <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-white/15" />
              <div>
                <div className="h-8 sm:h-10 w-20 rounded bg-white/15 mb-1" />
                <div className="h-3 w-24 rounded bg-white/10" />
              </div>
            </div>
          )}

          {/* Quick Jump Links */}
          {!isAllLocations && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-white/15">
              {[
                {
                  id: 'forecast',
                  label: '14-Day Forecast',
                  icon: <Calendar className="h-3.5 w-3.5" />,
                },
                { id: 'streaks', label: 'Streaks', icon: <Flame className="h-3.5 w-3.5" /> },
                { id: 'nearby', label: 'Nearby Cities', icon: <MapPin className="h-3.5 w-3.5" /> },
                {
                  id: 'on-this-day',
                  label: 'On This Day',
                  icon: <History className="h-3.5 w-3.5" />,
                },
              ].map((link) => (
                <button
                  key={link.id}
                  onClick={() =>
                    document
                      .getElementById(link.id)
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                >
                  {link.icon}
                  {link.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Back to previous location banner */}
      {previousLocation && (
        <div className="mb-6 flex items-center gap-3 bg-gray-800/80 backdrop-blur-sm border border-gray-700/50 rounded-xl px-4 py-3 animate-[slideDown_0.3s_ease-out]">
          <button
            onClick={() => {
              setSelectedLocation(previousLocation.id);
              addRecentLocation(previousLocation.id);
              setPreviousLocation(null);
              clearTimeout(previousLocationTimeoutRef.current);
            }}
            className="flex items-center gap-2 text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {previousLocation.name.split(',')[0]}
          </button>
          <div className="flex-1" />
          <button
            onClick={() => {
              setPreviousLocation(null);
              clearTimeout(previousLocationTimeoutRef.current);
            }}
            className="p-1 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <X className="h-3.5 w-3.5 text-gray-500" />
          </button>
        </div>
      )}

      {/* Quick links */}
      {!isAllLocations && selectedLocation && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
          <Link
            to={`/farming/${selectedLocation}`}
            className="flex items-center gap-3 bg-gray-800 hover:bg-gray-750 border border-gray-700/50 rounded-xl px-4 py-3 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-green-900/40 flex items-center justify-center flex-shrink-0">
              <Wheat className="h-5 w-5 text-green-400" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-100 group-hover:text-green-400 transition-colors">
                Farming Dashboard
              </div>
              <div className="text-xs text-gray-400 truncate">
                Frost risk, spray conditions, fire danger & more
              </div>
            </div>
          </Link>
          <Link
            to={`/outlook/${selectedLocation}`}
            className="flex items-center gap-3 bg-gray-800 hover:bg-gray-750 border border-gray-700/50 rounded-xl px-4 py-3 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-blue-900/40 flex items-center justify-center flex-shrink-0">
              <CalendarRange className="h-5 w-5 text-blue-400" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-100 group-hover:text-blue-400 transition-colors">
                Seasonal Outlook
              </div>
              <div className="text-xs text-gray-400 truncate">
                3-month forecast, rainfall trends & planting windows
              </div>
            </div>
          </Link>
          <Link
            to="/satellite"
            className="flex items-center gap-3 bg-gray-800 hover:bg-gray-750 border border-gray-700/50 rounded-xl px-4 py-3 transition-colors group"
          >
            <div className="w-10 h-10 rounded-lg bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
              <Satellite className="h-5 w-5 text-indigo-400" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-100 group-hover:text-indigo-400 transition-colors">
                Satellite & Radar
              </div>
              <div className="text-xs text-gray-400 truncate">
                Live Himawari-9 imagery, cloud movement & radar
              </div>
            </div>
          </Link>
        </div>
      )}

      {/* Content area */}
      <div>
        {/* Storm alert banner — shows when thunderstorm or heavy rain >70% probability within 48h */}
        {!isAllLocations && stormAlert && !stormAlertDismissed && (
          <div
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 mb-4 ${
              stormAlert.isThunderstorm
                ? 'bg-amber-950/60 border-amber-600/50'
                : 'bg-blue-950/60 border-blue-600/50'
            }`}
          >
            <AlertTriangle
              className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                stormAlert.isThunderstorm ? 'text-amber-400' : 'text-blue-400'
              }`}
            />
            <div className="flex-1 min-w-0">
              <p
                className={`text-sm font-semibold ${
                  stormAlert.isThunderstorm ? 'text-amber-300' : 'text-blue-300'
                }`}
              >
                {stormAlert.isThunderstorm ? 'Thunderstorm' : 'Heavy Rain'} approaching —{' '}
                {stormAlert.timeLabel}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {stormAlert.probability}% chance · Check the hourly forecast below for timing
              </p>
            </div>
            <button
              onClick={() => setStormAlertDismissed(true)}
              className="p-1 rounded hover:bg-white/10 transition-colors flex-shrink-0"
            >
              <X className="h-4 w-4 text-gray-400" />
            </button>
          </div>
        )}

        {/* BOM official weather warnings */}
        {!isAllLocations && bomAlerts.length > 0 && !bomAlertsDismissed && (
          <div className="mb-4 space-y-2">
            {bomAlerts.slice(0, 3).map((alert: WeatherAlert) => {
              const severityConfig = {
                extreme: {
                  bg: 'bg-red-950/70',
                  border: 'border-red-500/60',
                  text: 'text-red-300',
                  icon: 'text-red-400',
                },
                severe: {
                  bg: 'bg-orange-950/60',
                  border: 'border-orange-500/50',
                  text: 'text-orange-300',
                  icon: 'text-orange-400',
                },
                moderate: {
                  bg: 'bg-amber-950/60',
                  border: 'border-amber-600/50',
                  text: 'text-amber-300',
                  icon: 'text-amber-400',
                },
                minor: {
                  bg: 'bg-yellow-950/50',
                  border: 'border-yellow-600/40',
                  text: 'text-yellow-300',
                  icon: 'text-yellow-400',
                },
              }[alert.severity];

              return (
                <button
                  key={alert.id}
                  onClick={() => setSelectedBomAlert(alert)}
                  className={`flex items-start gap-3 rounded-xl border px-4 py-3 w-full text-left cursor-pointer hover:brightness-110 transition ${severityConfig.bg} ${severityConfig.border}`}
                >
                  <ShieldAlert className={`h-5 w-5 flex-shrink-0 mt-0.5 ${severityConfig.icon}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold ${severityConfig.text}`}>
                      BOM {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)}{' '}
                      {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}
                    </p>
                    <p className="text-xs text-gray-300 mt-0.5 line-clamp-2">{alert.title}</p>
                    {alert.areas.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Areas: {alert.areas.slice(0, 4).join(', ')}
                        {alert.areas.length > 4 && ` +${alert.areas.length - 4} more`}
                      </p>
                    )}
                  </div>
                  <span className="p-1 flex-shrink-0">
                    <ExternalLink className="h-4 w-4 text-gray-400" />
                  </span>
                </button>
              );
            })}
            {bomAlerts.length > 3 && (
              <p className="text-xs text-gray-500 text-center">
                +{bomAlerts.length - 3} more warning{bomAlerts.length - 3 > 1 ? 's' : ''}
              </p>
            )}
            <button
              onClick={() => setBomAlertsDismissed(true)}
              className="text-xs text-gray-500 hover:text-gray-300 transition-colors w-full text-center"
            >
              Dismiss warnings
            </button>
          </div>
        )}

        {/* Forecast Comparison: Open-Meteo vs Weathr Engine */}
        {loadTier >= 1 && !isAllLocations && selectedLocation && selectedLocationData && (
          <ForecastComparison locationId={selectedLocation} locationData={selectedLocationData} />
        )}

        {/* Compact rain radar mini-map */}
        {loadTier >= 2 && !isAllLocations && selectedLocation && selectedLocationData && (
          <WidgetBoundary>
            <div className="mb-6">
              <RadarMiniMap location={selectedLocationData} />
            </div>
          </WidgetBoundary>
        )}

        {/* AdSense — above stories */}
        {showAds && <AdSenseBanner slotId="top" responsive className="mb-6" />}

        {/* Stories Grid */}
        {isAllLocations ? (
          <Suspense
            fallback={
              <div className="@container mb-8">
                <div className="stories-grid">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-gray-800 rounded-xl p-6 animate-pulse border border-gray-700/50"
                    >
                      <div className="h-6 bg-gray-700 rounded w-1/4 mb-4" />
                      <div className="h-4 bg-gray-700 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-gray-700 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              </div>
            }
          >
            <AllLocationsStories locationId={selectedLocation} />
          </Suspense>
        ) : isLoading && !stories.length ? (
          <div className="mb-8">
            {/* Yesterday's stories strip while today loads */}
            <WidgetBoundary
              fallback={<div className="h-24 bg-gray-800/50 rounded-xl animate-pulse mb-4" />}
            >
              <YesterdayStoriesStrip
                locationId={selectedLocation}
                locationName={selectedLocationData?.name?.split(',')[0]}
              />
            </WidgetBoundary>
            <div className="@container">
              <div className="stories-grid">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="bg-gray-800 rounded-xl p-6 animate-pulse border border-gray-700/50"
                  >
                    <div className="h-6 bg-gray-700 rounded w-1/4 mb-4" />
                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-700 rounded w-1/2" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : stories.length > 0 ? (
          <div className="mb-8">
            <div className="@container">
              <div className="stories-grid">
                {stories.map((story, index) => {
                  const isNew = newStoryIds.has(story.id);
                  const newIdx = isNew ? [...newStoryIds].indexOf(story.id) : 0;
                  // Show site strip after story 3, then every 4 stories (3, 7, 11, 15...)
                  const showSiteStrip =
                    showAds && currentWeather && (index - 2) % 4 === 0 && index >= 2;
                  const delay = isNew ? newIdx * 0.08 : index * 0.025;

                  return (
                    <Fragment key={story.id}>
                      <div
                        className="bg-gray-800/50 rounded-xl border border-gray-700/50 h-full flex flex-col"
                        style={{ animation: `slideUp 0.22s ease-out ${delay}s both` }}
                      >
                        <StoryCard story={story} compact />
                      </div>
                      {/* Product recommendation after stories 3, 7, 11, 15... */}
                      {showSiteStrip && (
                        <WidgetBoundary fallback={null}>
                          <WeatherSiteStrip
                            temperature={
                              (currentWeather.temperature as number) ??
                              (currentWeather.tempMax as number)
                            }
                            conditions={currentWeather.conditions as string}
                            precipitation={(currentWeather.precipitation as number) ?? 0}
                            maxProducts={10}
                            compact
                            productIndex={Math.floor((index - 2) / 4)}
                          />
                        </WidgetBoundary>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            </div>
            {/* Generate more stories OR "check back tomorrow" */}
            {hasWeatherData &&
              (stories.length >= 16 ? (
                <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 flex flex-col items-center justify-center p-6 min-h-[120px]">
                  <Calendar className="h-8 w-8 text-gray-500 mb-2" />
                  <p className="text-gray-400 text-sm font-medium">All 16 stories for today</p>
                  <p className="text-gray-500 text-xs mt-1">Check back tomorrow for more!</p>
                </div>
              ) : (
                <div className="bg-gray-800/50 rounded-xl border border-dashed border-gray-600 flex flex-col items-center justify-center p-6 min-h-[120px]">
                  <form
                    action={async () => {
                      try {
                        await generateMutation.mutateAsync();
                      } catch {
                        // onError in mutation handles toast
                      }
                    }}
                  >
                    <GenerateStoriesButton
                      label="Generate More Stories"
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors disabled:opacity-50 font-medium"
                      isJobRunning={!!activeJobId}
                      jobProgress={jobStatusQuery.data?.progress}
                      jobTotal={jobStatusQuery.data?.total}
                      cooldownSeconds={cooldownSeconds}
                    />
                  </form>
                  <p className="text-gray-500 text-xs mt-2">{stories.length} of 16</p>
                </div>
              ))}
          </div>
        ) : (
          <div className="mb-8">
            <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700">
              <Sparkles className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-300 mb-2">
                No stories available for {selectedLocationData?.name || 'this location'} yet.
              </p>
              {!hasWeatherData ? (
                <p className="text-gray-500 text-sm">
                  No weather data available yet. Stories will appear once weather data has been
                  fetched.
                </p>
              ) : (
                <>
                  <p className="text-gray-500 text-sm mb-6">
                    Weather data is ready! Click "Generate Stories" above to create AI-powered
                    stories.
                  </p>
                  <form
                    action={async () => {
                      try {
                        await generateMutation.mutateAsync();
                      } catch {
                        // onError in mutation handles toast
                      }
                    }}
                  >
                    <GenerateStoriesButton
                      label="Generate Stories"
                      className="btn-primary flex items-center gap-2 mx-auto"
                      isJobRunning={!!activeJobId}
                      jobProgress={jobStatusQuery.data?.progress}
                      jobTotal={jobStatusQuery.data?.total}
                    />
                  </form>
                </>
              )}
            </div>

            {/* Stories from neighbouring towns, so an empty town is never a dead end */}
            {nearbyStories.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-primary-400" />
                  <h2 className="text-lg font-display font-semibold text-gray-100">
                    Stories from nearby towns
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {nearbyStories.slice(0, 6).map((story) => (
                    <div key={story.id}>
                      <p className="flex items-center gap-1 mb-1.5 text-xs text-gray-500">
                        <MapPin className="h-3 w-3" />
                        {[story.location?.name, story.location?.state].filter(Boolean).join(', ')}
                        {story.distanceKm != null && story.distanceKm > 0 && (
                          <span> · {story.distanceKm} km away</span>
                        )}
                      </p>
                      <StoryCard story={story} compact />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Yesterday's Recap — permanent collapsible widget */}
        {!isAllLocations && selectedLocation && (
          <WidgetBoundary
            fallback={
              <div className="h-16 bg-gray-800 rounded-xl border border-gray-700/50 animate-pulse mb-6" />
            }
          >
            <YesterdayRecapWidget
              locationId={selectedLocation}
              locationName={selectedLocationData?.name?.split(',')[0]}
              className="mb-6"
            />
          </WidgetBoundary>
        )}

        {/* This Week's Best — top stories by engagement */}
        {!isAllLocations && selectedLocation && loadTier >= 3 && (
          <WidgetBoundary
            fallback={
              <div className="h-32 bg-gray-800 rounded-xl border border-gray-700/50 animate-pulse mb-6" />
            }
          >
            <WeeklyBestWidget locationId={selectedLocation} className="mb-6" />
          </WidgetBoundary>
        )}

        {/* AdSense — below stories */}
        {showAds && <AdSenseBanner slotId="mid" responsive className="mb-6" />}

        {/* Weather Trivia (Tier 3) */}
        {loadTier >= 3 && !isAllLocations && (
          <WidgetBoundary
            fallback={
              <div className="h-24 bg-gray-800 rounded-xl border border-gray-700/50 animate-pulse mb-8" />
            }
          >
            <WeatherTriviaWidget
              temp={headerTemp}
              conditions={headerCondition}
              locationName={selectedLocationData?.name}
              state={selectedLocationData?.state}
              className="mb-8"
            />
          </WidgetBoundary>
        )}

        {/* On This Day Historical Widget */}
        <div id="on-this-day" className="scroll-mt-4" />
        {loadTier >= 2 && !isAllLocations && selectedLocation && selectedLocationData && (
          <WidgetBoundary
            fallback={
              <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6 mb-8">
                <OnThisDaySkeleton />
              </div>
            }
          >
            <OnThisDayWidget
              locationId={selectedLocation}
              locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
              className="mb-8"
            />
          </WidgetBoundary>
        )}

        {/* Nearby Cities (Tier 2) */}
        <div id="nearby" className="scroll-mt-4" />
        {loadTier >= 2 && !isAllLocations && selectedLocation && (
          <WidgetBoundary
            fallback={
              <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6 mb-8">
                <NearbyCitiesSkeleton />
              </div>
            }
          >
            <NearbyCitiesWidget
              locationId={selectedLocation}
              onLocationSelect={(id) => {
                // Track previous location for "go back" banner
                if (selectedLocationData) {
                  setPreviousLocation({
                    id: selectedLocation,
                    name: `${selectedLocationData.name}, ${selectedLocationData.state || selectedLocationData.country}`,
                  });
                  // Auto-dismiss after 8 seconds
                  clearTimeout(previousLocationTimeoutRef.current);
                  previousLocationTimeoutRef.current = setTimeout(
                    () => setPreviousLocation(null),
                    8000
                  );
                }
                setSelectedLocation(id);
                addRecentLocation(id);
              }}
              className="mb-8"
            />
          </WidgetBoundary>
        )}

        {/* Premium product strip before forecast for unsubscribed users */}
        {showAds && !isAllLocations && currentWeather && (
          <div className="mb-8">
            <WidgetBoundary
              fallback={<div className="h-32 bg-gray-800 rounded-xl animate-pulse" />}
            >
              <WeatherSiteStrip
                temperature={
                  (currentWeather.temperature as number) ?? (currentWeather.tempMax as number)
                }
                conditions={currentWeather.conditions as string}
                precipitation={(currentWeather.precipitation as number) ?? 0}
                maxProducts={10}
              />
            </WidgetBoundary>
          </div>
        )}

        {/* Weather Widgets (Tier 2+) */}
        <div id="forecast" className="scroll-mt-4" />
        {loadTier >= 2 && !isAllLocations && selectedLocation && selectedLocationData && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* AdSense — between forecasts */}
            {showAds && (
              <div className="lg:col-span-2">
                <AdSenseBanner slotId="forecast" responsive />
              </div>
            )}

            {/* Weekly Forecast Grid (Tier 2) */}
            <div id="forecast" className="lg:col-span-2 scroll-mt-4">
              <WidgetBoundary
                fallback={
                  <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6">
                    <WeeklyForecastSkeleton />
                  </div>
                }
              >
                <WeeklyForecastGrid
                  locationId={selectedLocation}
                  locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
                  days={16}
                  allowExtendedToggle
                  defaultExtended
                />
              </WidgetBoundary>
            </div>

            {/* Active Streaks Widget (Australian locations only) */}
            {selectedLocationData?.country === 'Australia' && (
              <div id="streaks" className="lg:col-span-2 scroll-mt-4">
                <WidgetBoundary
                  fallback={
                    <div className="h-48 bg-gray-800 rounded-xl border border-gray-700/50 animate-pulse" />
                  }
                >
                  <ActiveStreaksWidget
                    locationId={selectedLocation}
                    locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
                  />
                </WidgetBoundary>
              </div>
            )}

            {/* Streak Leaderboard */}
            <WidgetBoundary
              fallback={
                <div className="h-48 bg-gray-800 rounded-xl border border-gray-700/50 animate-pulse lg:col-span-2" />
              }
            >
              <StreakLeaderboardWidget
                currentLocationId={selectedLocation}
                className="lg:col-span-2"
              />
            </WidgetBoundary>

            {/* Streak Heatmap */}
            <WidgetBoundary
              fallback={
                <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6 lg:col-span-2">
                  <StreakHeatmapSkeleton />
                </div>
              }
            >
              <StreakHeatmapWidget
                locationId={selectedLocation}
                locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
                className="lg:col-span-2"
              />
            </WidgetBoundary>

            {/* Tier 3: Will It Rain & Air Quality side by side */}
            {loadTier >= 3 && (
              <>
                <WidgetBoundary
                  fallback={
                    <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6">
                      <WillItRainSkeleton />
                    </div>
                  }
                >
                  <WillItRainWidget
                    locationId={selectedLocation}
                    locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
                  />
                </WidgetBoundary>

                <WidgetBoundary
                  fallback={
                    <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6">
                      <AirQualitySkeleton />
                    </div>
                  }
                >
                  <AirQualityWidget
                    locationId={selectedLocation}
                    locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
                  />
                </WidgetBoundary>
              </>
            )}

            {/* Nearest Station Finder */}
            <WidgetBoundary
              fallback={
                <div className="h-48 bg-gray-800 rounded-xl border border-gray-700/50 animate-pulse" />
              }
            >
              <NearestStationFinder
                onSelect={(location) => {
                  if (selectedLocationData) {
                    setPreviousLocation({
                      id: selectedLocation,
                      name: `${selectedLocationData.name}, ${selectedLocationData.state || selectedLocationData.country}`,
                    });
                    clearTimeout(previousLocationTimeoutRef.current);
                    previousLocationTimeoutRef.current = setTimeout(
                      () => setPreviousLocation(null),
                      8000
                    );
                  }
                  handleLocationSelect(location.id);
                }}
              />
            </WidgetBoundary>

            {/* Tier 3: Compare Dates / Event Planner Widget - Full width */}
            {loadTier >= 3 && (
              <WidgetBoundary
                fallback={
                  <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6 lg:col-span-2">
                    <CompareDatesSkeleton />
                  </div>
                }
              >
                <CompareDatesWidget
                  locationId={selectedLocation}
                  locationName={`${selectedLocationData.name}, ${selectedLocationData.state}`}
                  className="lg:col-span-2"
                />
              </WidgetBoundary>
            )}
          </div>
        )}

        {/* Astronomical Widget (Tier 3) */}
        {loadTier >= 3 && !isAllLocations && selectedLocation && selectedLocationData && (
          <WidgetBoundary
            fallback={
              <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-6 mb-8">
                <AstronomicalSkeleton />
              </div>
            }
          >
            <AstronomicalWidget
              locationId={selectedLocation}
              locationName={`${selectedLocationData.name}, ${selectedLocationData.state || selectedLocationData.country}`}
              latitude={selectedLocationData.latitude}
              className="mb-8"
            />
          </WidgetBoundary>
        )}

        {/* Upgrade banner for free users */}
        {showAds && !isAllLocations && <UpgradeBanner className="mb-8" />}

        {/* Ad-blocker detection — gentle subscription nudge */}
        {showAds && <AdBlockDetector className="mb-8" />}

        {/* Trending / Cross-Location Highlights */}
        {!isAllLocations && trendingStories.length > 0 && (
          <div className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="h-5 w-5 text-orange-400" />
              <h2 className="text-xl font-semibold text-gray-100">Trending Across Australia</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {trendingStories.map((story) => (
                <div key={story.id} className="bg-gray-800/50 rounded-xl border border-gray-700/50">
                  <StoryCard story={story} compact />
                </div>
              ))}
            </div>
          </div>
        )}
        {!isAllLocations && trendingStories.length === 0 && crossLocationStories.length > 0 && (
          <FadeInWhenVisible className="mt-10">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="h-5 w-5 text-primary-400" />
              <h2 className="text-xl font-semibold text-gray-100">Stories from Other Locations</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {crossLocationStories.map((story) => (
                <div key={story.id} className="bg-gray-800/50 rounded-xl border border-gray-700/50">
                  <StoryCard story={story} compact />
                </div>
              ))}
            </div>
          </FadeInWhenVisible>
        )}

        {/* AdSense — above footer */}
        {showAds && <AdSenseBanner slotId="bottom" responsive className="mt-8" />}
      </div>

      {/* BOM Alert Detail Modal */}
      {selectedBomAlert && (
        <BomAlertModal alert={selectedBomAlert} onClose={() => setSelectedBomAlert(null)} />
      )}
    </div>
  );
}
